import React, { useState, useCallback, useEffect } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import WithWizard from 'modules/views/shared/Wizard/WithWizard';
import SkippableFieldsModal from 'modules/views/shared/SkippableFieldsModal';
import NavigationButtons from 'modules/views/shared/NavigationButtons';
import { ContactInfoSection, HomeAddressSection, EmergencyContactSection, SectionDivider } from './components';

import { useWizardStepNavigation } from 'modules/views/shared/Wizard';
import { getSkippedFields } from 'utils/skippableFieldsUtils';
import { useContactInformationFields } from 'hooks/useContactInformationFields';
import { useAppSelector } from 'modules/state-mgmt/store';
import { useContactInformation, useContactWizardFields } from './hooks';

import { useStyles } from './styles';

const ContactInformation = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { handlePreviousStep, canGoToPreviousStep, getNextStepName } = useWizardStepNavigation();

  // State for skippable fields modal
  const [showSkippableFieldsModal, setShowSkippableFieldsModal] = useState(false);

  // Get profile field constraints and hasReachedConsentForm from Redux
  const { profileFieldConstraintsMap, hasReachedConsentForm, isArchivedProject } = useAppSelector(state => ({
    profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap,
    hasReachedConsentForm: state.onboarding.temp?.hasReachedConsentForm,
    isArchivedProject: state.cache.project.status === 3,
  }));

  // Get field configuration for skippable fields (following PersonalInformation pattern)
  const { fieldDisplayNames, getDynamicFormFieldMap, labels, workerProfileFields } = useContactInformationFields();

  // Single master hook for all functionality with built-in form submission
  const {
    sectionsConfig,
    contactForm,
    formErrors,
    handleFieldChange,
    handlePhoneChange,
    handleDirectFieldUpdate,
    handleSubmit: originalHandleSubmit,
    canEditEmail,
    canEditMobilePhoneNumber,
    isSubmitting,
  } = useContactInformation();

  // Generate fields for WithWizard sidebar
  const fields = useContactWizardFields(sectionsConfig);

  // Handle when user chooses to continue with skipped fields
  const handleContinueWithSkippedFields = useCallback(async () => {
    setShowSkippableFieldsModal(false);
    await originalHandleSubmit();
  }, [originalHandleSubmit]);

  // Handle form submission with skippable fields check
  const handleSubmit = useCallback(async () => {
    // Check for skippable fields:
    // - Before consent form: All skippable fields trigger warning
    // - After consent form: Only Badge Printing/Activation fields trigger warning
    const dynamicFormFieldMap = getDynamicFormFieldMap(contactForm);
    const skippedFields = getSkippedFields(
      contactForm,
      profileFieldConstraintsMap,
      hasReachedConsentForm,
      fieldDisplayNames,
      dynamicFormFieldMap,
    );

    if (skippedFields.length > 0) {
      setShowSkippableFieldsModal(true);
      return;
    }

    // Proceed with normal form submission
    await originalHandleSubmit();
  }, [
    hasReachedConsentForm,
    getDynamicFormFieldMap,
    contactForm,
    profileFieldConstraintsMap,
    fieldDisplayNames,
    originalHandleSubmit,
  ]);

  // Calculate skipped fields for the modal
  const skippedFields = showSkippableFieldsModal
    ? getSkippedFields(
        contactForm,
        profileFieldConstraintsMap,
        hasReachedConsentForm,
        fieldDisplayNames,
        getDynamicFormFieldMap(contactForm),
      )
    : [];

  const content = (
    <div className={classes.root}>
      <ContentHeader title={t('contactInformation.title')} subtitle={t('contactInformation.subtitle')} />

      <Grid container className={classes.mainContainer}>
        <ContactInfoSection
          config={sectionsConfig.contactInfo}
          contactForm={contactForm}
          formErrors={formErrors}
          onFieldChange={handleFieldChange}
          onPhoneChange={handlePhoneChange}
          canEditEmail={canEditEmail}
          canEditMobilePhoneNumber={canEditMobilePhoneNumber}
          isSubmitting={isSubmitting}
          sectionClasses={classes.section}
          sectionTitleClasses={classes.sectionTitle}
          labels={labels} // Pass centralized labels
          workerProfileFields={workerProfileFields} // Pass WorkerProfileField mappings
          isArchivedProject={isArchivedProject}
        />

        <SectionDivider
          isVisible={
            sectionsConfig.contactInfo.isVisible &&
            (sectionsConfig.homeAddress.isVisible || sectionsConfig.emergencyContact.isVisible)
          }
          dividerClasses={classes.divider}
        />

        <HomeAddressSection
          config={sectionsConfig.homeAddress}
          contactForm={contactForm}
          formErrors={formErrors}
          onFieldChange={handleFieldChange}
          onDirectFieldUpdate={handleDirectFieldUpdate}
          isSubmitting={isSubmitting}
          sectionClasses={classes.section}
          sectionTitleClasses={classes.sectionTitle}
          labels={labels} // Pass centralized labels
          workerProfileFields={workerProfileFields} // Pass WorkerProfileField mappings
          isArchivedProject={isArchivedProject}
        />

        <SectionDivider
          isVisible={sectionsConfig.homeAddress.isVisible && sectionsConfig.emergencyContact.isVisible}
          dividerClasses={classes.divider}
        />

        <EmergencyContactSection
          config={sectionsConfig.emergencyContact}
          contactForm={contactForm}
          formErrors={formErrors}
          onFieldChange={handleFieldChange}
          onPhoneChange={handlePhoneChange}
          isSubmitting={isSubmitting}
          sectionClasses={classes.section}
          sectionTitleClasses={classes.sectionTitle}
          labels={labels}
          workerProfileFields={workerProfileFields}
          isArchivedProject={isArchivedProject}
        />
      </Grid>

      <NavigationButtons
        secondaryButtonProps={{
          isHidden: !canGoToPreviousStep,
          onClick: handlePreviousStep,
          testId: 'contact-info-back-button',
        }}
        primaryButtonProps={{
          label: getNextStepName() ? `${t('common.next')}: ${getNextStepName()}` : t('contactInformation.next'),
          isLoading: isSubmitting,
          hasEndArrow: true,
          testId: 'contact-info-next-button',
          onClick: handleSubmit,
        }}
      />

      {showSkippableFieldsModal && (
        <SkippableFieldsModal
          isVisible={showSkippableFieldsModal}
          skippedFields={skippedFields}
          isLoading={isSubmitting}
          onContinue={handleContinueWithSkippedFields}
          onFillFields={() => setShowSkippableFieldsModal(false)}
          onClose={() => setShowSkippableFieldsModal(false)}
        />
      )}
    </div>
  );

  return <WithWizard disableNavigation={isSubmitting} fields={fields} content={content} />;
};

export default ContactInformation;
